import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Header
    nav: {
      services: 'Services',
      portfolio: 'Portfolio',
      whyUs: 'Why Us',
      contact: 'Contact',
      getStarted: 'Get Started',
    },
    // Hero
    hero: {
      badge: 'AI-Powered Marketing Agency',
      headline1: 'Scale Your Brand With',
      headline2: 'AI',
      headline3: 'Marketing',
      subheadline: 'We combine cutting-edge AI technology with proven marketing strategies to deliver',
      subheadlineBold: 'explosive growth',
      subheadlineEnd: 'for your business. Results guaranteed.',
      cta: 'Get Free Consultation',
      viewWork: 'View Our Work',
      clients: '400+ Clients Worldwide',
      trustedBy: 'Trusted by leading brands',
      roiIncrease: 'ROI Increase',
      viewsGenerated: 'Views Generated',
    },
    // Social Proof
    socialProof: {
      overline: 'Proven Results',
      headline: 'Numbers That Speak',
      clients: 'Clients Worked With',
      clientsDesc: 'Businesses trust us',
      views: 'Content Views Generated',
      viewsDesc: 'Organic reach achieved',
      projects: 'Projects Completed',
      projectsDesc: 'Successfully delivered',
      roi: 'Max ROI Increase',
      roiDesc: 'For our clients',
      trustedBy: 'Trusted by innovative brands',
    },
    // ROI
    roi: {
      overline: 'Return on Investment',
      headline: 'We Deliver',
      headlineAccent: 'Measurable Growth',
      subheadline: "We don't just build websites and campaigns — we deliver measurable growth.",
      basedOn: 'Based on work with',
      clientsWorldwide: 'clients worldwide',
      roiIncrease: 'Average ROI Increase',
      roiDesc: 'Return on marketing investment',
      conversion: 'Conversion Rate Improvement',
      conversionDesc: 'More leads, more customers',
      cpl: 'Cost Per Lead Reduction',
      cplDesc: 'Lower acquisition costs',
      focusText: 'Our focus is simple:',
      focusBold: 'turn marketing budgets into scalable profit systems.',
      strategyText: 'Every strategy optimized for maximum return on investment',
      dataDriven: 'Data-Driven Strategies',
      transparent: 'Transparent Reporting',
      guaranteed: 'Performance Guaranteed',
    },
    // Services
    services: {
      overline: 'What We Do',
      headline: 'Services That Drive',
      headlineAccent: 'Growth',
      subheadline: 'End-to-end marketing solutions powered by AI to transform your brand and scale your business.',
      cta: 'Discuss Your Project',
      learnMore: 'Learn More',
      // Service items
      videoTitle: 'AI Video Production',
      videoDesc: 'High-conversion video creatives powered by cutting-edge AI. From concept to viral content.',
      videoF1: 'Viral-ready content',
      videoF2: 'AI-enhanced editing',
      videoF3: 'Platform optimized',
      metaTitle: 'Meta Ads Management',
      metaDesc: 'Data-driven Instagram & Facebook ad campaigns that scale your business predictably.',
      metaF1: 'ROI focused',
      metaF2: 'A/B testing',
      metaF3: 'Real-time optimization',
      brandingTitle: 'Branding & Content',
      brandingDesc: 'Build a million-dollar brand presence that commands attention and drives conversions.',
      brandingF1: 'Brand identity',
      brandingF2: 'Content strategy',
      brandingF3: 'Visual design',
    },
    // Web Design
    webDesign: {
      overline: 'Website Design',
      headline: 'Websites That',
      headlineAccent: 'Bring Customers',
      subheadline: 'We create modern, high-converting websites that help businesses grow.',
      focus: 'Focus on performance, user experience, and results — not just design.',
      keyMessage: 'We build websites that bring customers,',
      keyMessageAccent: 'not just look good.',
      cta: 'Get a Free Quote',
      feature1: 'Modern & Premium Design',
      feature1Desc: 'Clean, professional aesthetics that reflect your brand quality.',
      feature2: 'Mobile-Friendly Layouts',
      feature2Desc: 'Responsive design that works flawlessly on all devices.',
      feature3: 'Fast & Optimized Performance',
      feature3Desc: 'Lightning-fast load times for better user experience and SEO.',
      feature4: 'Conversion-Focused Structure',
      feature4Desc: 'Strategic layouts designed to turn visitors into customers.',
      trust1: 'Custom Development',
      trust2: 'SEO Optimized',
      trust3: 'CMS Integration',
      trust4: 'Ongoing Support',
    },
    // Portfolio
    portfolio: {
      overline: 'Our Work',
      headline: 'Results That',
      headlineAccent: 'Impress',
      subheadline: "A glimpse of the campaigns we've executed and the growth we've delivered for our clients.",
      viewMore: 'Want to see more?',
      contactUs: 'Contact us for our full portfolio',
    },
    // Why Us
    whyUs: {
      overline: 'Why Sentetica',
      headline: "We Don't Just Market.",
      headlineAccent: 'We Transform.',
      subheadline: 'In a world of noise, we create signals. Our AI-powered approach ensures your brand reaches the right audience at the right time with the right message.',
      retention: 'Client Retention',
      response: 'Response Time',
      experience: 'Years Experience',
      cta: "Let's Talk Strategy",
      feature1: 'Data-Driven AI Solutions',
      feature1Desc: 'We leverage cutting-edge AI algorithms to analyze market trends and optimize every campaign for maximum impact.',
      feature2: 'Proven ROI & Scale',
      feature2Desc: 'Our strategies have consistently delivered 3x+ returns. We focus on metrics that matter to your bottom line.',
      feature3: 'Premium Brand Aesthetic',
      feature3Desc: 'Every piece of content we create is designed to position your brand as a market leader.',
      feature4: 'End-to-End Automation',
      feature4Desc: 'From lead generation to customer retention, we automate your marketing funnel for 24/7 growth.',
      badge1: 'Meta Business Partner',
      badge2: 'Google Certified',
      badge3: 'AI Certified Agency',
      badge4: 'Top Rated 2024',
    },
    // Contact
    contact: {
      overline: 'Get In Touch',
      headline: 'Ready to',
      headlineAccent: 'Scale?',
      subheadline: "Let's discuss how we can transform your marketing and drive explosive growth for your business.",
      respondsIn: 'Usually responds within 2 hours',
      fastestWay: 'Fastest Way to Reach Us?',
      skipWait: "Skip the wait. Message us directly on WhatsApp for an instant consultation. Our team is ready to discuss your project and provide a custom quote.",
      whatsappBtn: 'Message Us on WhatsApp',
      freeConsultation: 'Free Consultation',
      quickResponse: 'Quick Response',
      limitedTime: 'Limited Time: Free Strategy Call',
      limitedTimeDesc: 'Book your free 30-minute strategy session. No strings attached.',
      orLeaveMessage: 'Or Leave a Message',
      getBack: "We'll get back to you within 24 hours.",
      yourName: 'Your Name',
      companyName: 'Company Name',
      yourMessage: 'Your Message',
      messagePlaceholder: 'Tell us about your project and goals...',
      sendMessage: 'Send Message',
      messageSent: 'Message Sent!',
      thankYou: "Thanks for reaching out. We'll get back to you soon.",
      chatNow: 'Or chat with us now on WhatsApp',
    },
    // Footer
    footer: {
      ctaHeadline: 'Ready to',
      ctaAccent: 'Transform',
      ctaEnd: 'Your Marketing?',
      ctaSubheadline: "Join 400+ businesses that trust Sentetica for their growth. Let's build something amazing together.",
      ctaBtn: 'Start a Conversation',
      description: 'AI-powered marketing agency helping brands scale with data-driven strategies, automation, and premium creative content.',
      servicesTitle: 'Services',
      companyTitle: 'Company',
      whatsappUs: 'WhatsApp Us',
      copyright: 'All rights reserved.',
    },
  },
  tr: {
    // Header
    nav: {
      services: 'Hizmetler',
      portfolio: 'Portfolyo',
      whyUs: 'Neden Biz',
      contact: 'İletişim',
      getStarted: 'Başlayın',
    },
    // Hero
    hero: {
      badge: 'Yapay Zeka Destekli Pazarlama Ajansı',
      headline1: 'Markanızı',
      headline2: 'AI',
      headline3: 'Pazarlama ile Büyütün',
      subheadline: 'Kanıtlanmış pazarlama stratejileri ile son teknoloji yapay zekayı birleştirerek işletmeniz için',
      subheadlineBold: 'patlayıcı büyüme',
      subheadlineEnd: 'sağlıyoruz. Sonuçlar garantili.',
      cta: 'Ücretsiz Danışmanlık Alın',
      viewWork: 'Çalışmalarımızı Görün',
      clients: '400+ Dünya Genelinde Müşteri',
      trustedBy: 'Lider markaların güveni',
      roiIncrease: 'ROI Artışı',
      viewsGenerated: 'Görüntülenme',
    },
    // Social Proof
    socialProof: {
      overline: 'Kanıtlanmış Sonuçlar',
      headline: 'Konuşan Rakamlar',
      clients: 'Çalıştığımız Müşteri',
      clientsDesc: 'Bize güvenen işletmeler',
      views: 'İçerik Görüntülenmesi',
      viewsDesc: 'Organik erişim',
      projects: 'Tamamlanan Proje',
      projectsDesc: 'Başarıyla teslim edildi',
      roi: 'Maksimum ROI Artışı',
      roiDesc: 'Müşterilerimiz için',
      trustedBy: 'Yenilikçi markaların güveni',
    },
    // ROI
    roi: {
      overline: 'Yatırım Getirisi',
      headline: 'Ölçülebilir',
      headlineAccent: 'Büyüme Sağlıyoruz',
      subheadline: 'Sadece web sitesi ve kampanya oluşturmuyoruz — ölçülebilir büyüme sağlıyoruz.',
      basedOn: 'Dünya genelinde',
      clientsWorldwide: 'müşteri deneyimine dayalı',
      roiIncrease: 'Ortalama ROI Artışı',
      roiDesc: 'Pazarlama yatırım getirisi',
      conversion: 'Dönüşüm Oranı İyileştirmesi',
      conversionDesc: 'Daha fazla lead, daha fazla müşteri',
      cpl: 'Lead Başına Maliyet Düşüşü',
      cplDesc: 'Daha düşük edinim maliyetleri',
      focusText: 'Odağımız basit:',
      focusBold: 'pazarlama bütçelerini ölçeklenebilir kâr sistemlerine dönüştürmek.',
      strategyText: 'Her strateji maksimum yatırım getirisi için optimize edilmiştir',
      dataDriven: 'Veri Odaklı Stratejiler',
      transparent: 'Şeffaf Raporlama',
      guaranteed: 'Performans Garantisi',
    },
    // Services
    services: {
      overline: 'Ne Yapıyoruz',
      headline: 'Büyüme Sağlayan',
      headlineAccent: 'Hizmetler',
      subheadline: 'Markanızı dönüştürmek ve işinizi büyütmek için yapay zeka destekli uçtan uca pazarlama çözümleri.',
      cta: 'Projenizi Tartışalım',
      learnMore: 'Daha Fazla',
      // Service items
      videoTitle: 'AI Video Prodüksiyonu',
      videoDesc: 'Son teknoloji yapay zeka ile güçlendirilmiş yüksek dönüşümlü video içerikleri. Konseptten viral içeriğe.',
      videoF1: 'Viral-hazır içerik',
      videoF2: 'AI destekli düzenleme',
      videoF3: 'Platform optimizasyonu',
      metaTitle: 'Meta Reklam Yönetimi',
      metaDesc: 'İşletmenizi öngörülebilir şekilde büyüten veri odaklı Instagram & Facebook reklam kampanyaları.',
      metaF1: 'ROI odaklı',
      metaF2: 'A/B testi',
      metaF3: 'Gerçek zamanlı optimizasyon',
      brandingTitle: 'Marka & İçerik',
      brandingDesc: 'Dikkat çeken ve dönüşüm sağlayan milyonluk bir marka varlığı oluşturun.',
      brandingF1: 'Marka kimliği',
      brandingF2: 'İçerik stratejisi',
      brandingF3: 'Görsel tasarım',
    },
    // Web Design
    webDesign: {
      overline: 'Web Sitesi Tasarımı',
      headline: 'Müşteri Getiren',
      headlineAccent: 'Web Siteleri',
      subheadline: 'İşletmelerin büyümesine yardımcı olan modern, yüksek dönüşümlü web siteleri oluşturuyoruz.',
      focus: 'Performans, kullanıcı deneyimi ve sonuçlara odaklanıyoruz — sadece tasarıma değil.',
      keyMessage: 'Sadece güzel görünen değil,',
      keyMessageAccent: 'müşteri getiren web siteleri yapıyoruz.',
      cta: 'Ücretsiz Teklif Alın',
      feature1: 'Modern & Premium Tasarım',
      feature1Desc: 'Marka kalitenizi yansıtan temiz, profesyonel estetik.',
      feature2: 'Mobil Uyumlu Tasarım',
      feature2Desc: 'Tüm cihazlarda kusursuz çalışan responsive tasarım.',
      feature3: 'Hızlı & Optimize Performans',
      feature3Desc: 'Daha iyi kullanıcı deneyimi ve SEO için yıldırım hızında yüklenme.',
      feature4: 'Dönüşüm Odaklı Yapı',
      feature4Desc: 'Ziyaretçileri müşteriye dönüştürmek için stratejik düzenler.',
      trust1: 'Özel Geliştirme',
      trust2: 'SEO Optimize',
      trust3: 'CMS Entegrasyonu',
      trust4: 'Sürekli Destek',
    },
    // Portfolio
    portfolio: {
      overline: 'Çalışmalarımız',
      headline: 'Etkileyen',
      headlineAccent: 'Sonuçlar',
      subheadline: 'Yürüttüğümüz kampanyalardan ve müşterilerimize sağladığımız büyümeden bir kesit.',
      viewMore: 'Daha fazlasını görmek ister misiniz?',
      contactUs: 'Tam portfolyo için bizimle iletişime geçin',
    },
    // Why Us
    whyUs: {
      overline: 'Neden Sentetica',
      headline: 'Sadece Pazarlamıyoruz.',
      headlineAccent: 'Dönüştürüyoruz.',
      subheadline: 'Gürültü dolu bir dünyada sinyaller yaratıyoruz. Yapay zeka destekli yaklaşımımız, markanızın doğru zamanda doğru kitleye doğru mesajla ulaşmasını sağlıyor.',
      retention: 'Müşteri Sadakati',
      response: 'Yanıt Süresi',
      experience: 'Yıl Deneyim',
      cta: 'Strateji Konuşalım',
      feature1: 'Veri Odaklı AI Çözümleri',
      feature1Desc: 'Pazar trendlerini analiz etmek ve her kampanyayı maksimum etki için optimize etmek için son teknoloji AI algoritmaları kullanıyoruz.',
      feature2: 'Kanıtlanmış ROI & Ölçek',
      feature2Desc: 'Stratejilerimiz tutarlı olarak 3x+ getiri sağladı. Kâr haneniz için önemli olan metriklere odaklanıyoruz.',
      feature3: 'Premium Marka Estetiği',
      feature3Desc: 'Oluşturduğumuz her içerik, markanızı pazar lideri olarak konumlandırmak için tasarlanmıştır.',
      feature4: 'Uçtan Uca Otomasyon',
      feature4Desc: 'Lead oluşturmadan müşteri tutmaya, pazarlama huninizi 7/24 büyüme için otomatikleştiriyoruz.',
      badge1: 'Meta İş Ortağı',
      badge2: 'Google Sertifikalı',
      badge3: 'AI Sertifikalı Ajans',
      badge4: 'En İyi 2024',
    },
    // Contact
    contact: {
      overline: 'İletişime Geçin',
      headline: 'Büyümeye Hazır',
      headlineAccent: 'mısınız?',
      subheadline: 'Pazarlamanızı nasıl dönüştürebileceğimizi ve işletmeniz için patlayıcı büyüme nasıl sağlayabileceğimizi konuşalım.',
      respondsIn: 'Genellikle 2 saat içinde yanıt verir',
      fastestWay: 'Bize Ulaşmanın En Hızlı Yolu?',
      skipWait: 'Beklemeyin. Anında danışmanlık için WhatsApp üzerinden doğrudan mesaj gönderin. Ekibimiz projenizi tartışmaya ve özel bir teklif sunmaya hazır.',
      whatsappBtn: "WhatsApp'tan Mesaj Gönderin",
      freeConsultation: 'Ücretsiz Danışmanlık',
      quickResponse: 'Hızlı Yanıt',
      limitedTime: 'Sınırlı Süre: Ücretsiz Strateji Görüşmesi',
      limitedTimeDesc: 'Ücretsiz 30 dakikalık strateji oturumunuzu ayırtın. Herhangi bir taahhüt yok.',
      orLeaveMessage: 'Veya Mesaj Bırakın',
      getBack: '24 saat içinde size geri döneceğiz.',
      yourName: 'Adınız',
      companyName: 'Şirket Adı',
      yourMessage: 'Mesajınız',
      messagePlaceholder: 'Projeniz ve hedefleriniz hakkında bilgi verin...',
      sendMessage: 'Mesaj Gönder',
      messageSent: 'Mesaj Gönderildi!',
      thankYou: 'Bize ulaştığınız için teşekkürler. En kısa sürede size döneceğiz.',
      chatNow: "Veya şimdi WhatsApp'tan yazın",
    },
    // Footer
    footer: {
      ctaHeadline: 'Pazarlamanızı',
      ctaAccent: 'Dönüştürmeye',
      ctaEnd: 'Hazır mısınız?',
      ctaSubheadline: "Büyümesi için Sentetica'ya güvenen 400+ işletmeye katılın. Birlikte harika şeyler inşa edelim.",
      ctaBtn: 'Sohbet Başlatın',
      description: 'Veri odaklı stratejiler, otomasyon ve premium yaratıcı içeriklerle markaların ölçeklenmesine yardımcı olan yapay zeka destekli pazarlama ajansı.',
      servicesTitle: 'Hizmetler',
      companyTitle: 'Şirket',
      whatsappUs: "WhatsApp'tan Yazın",
      copyright: 'Tüm hakları saklıdır.',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sentetica-lang') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('sentetica-lang', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'tr' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
